import React from 'react';

import DataTable from 'react-data-table-component';
import { tableStyles } from '../../styles/components/js/tableStyle';
import i18n from '../../i18n';
import Tooltip from '../common/Tooltip';
import { Check, Info, X } from 'lucide-react';
import { formatDate } from '../../utils/common';

const types = {
  enroll: i18n.t('enroll'),
  sitevisit: i18n.t('site_visit'),
  online: i18n.t('online'),
  offline: i18n.t('offline'),
  wallet: i18n.t('wallet'),
};

const statuses = {
  A: 'Waiting for PM Approval',
  PA: 'Refund Approved by PM',
  GA: 'Waiting for CEO Approval',
  CA: 'Refund Approved by CEO',
  RR: 'Refund Rejected',
  P: 'Paid',
  N: 'Not Paid',
  R: 'Refunded',
};

const statusColors = {
  A: '#f59e0b', // amber-500
  PA: '#10b981', // emerald-500
  GA: '#3b82f6', // blue-500
  CA: '#10b981', // emerald-500
  RR: '#ef4444', // red-500
  P: '#10b981', // emerald-500
  N: '#ef4444', // red-500
  R: '#f59e0b', // amber-500
};
const StatusBadge = ({ status }) => {
  const color = statusColors[status] || '#6b7280';
  const text = statuses[status] || i18n.t('unknown_status');

  return (
    <span
      style={{
        backgroundColor: `${color}15`,
        color: color,
        padding: '4px 12px',
        borderRadius: '0.3rem',
        fontSize: '12px',
        letterSpacing: '0.025em',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );
};

export class PaymentsList extends React.Component {
  constructor() {
    super();
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleRowsPerPageChange = this.handleRowsPerPageChange.bind(this);
  }

  handlePageChange = (page) => {
    if (this.props.onChangePage) {
      this.props.onChangePage(page);
    }
  };

  handleRowsPerPageChange = (newLimit, page) => {
    if (this.props.onChangeRowsPerPage) {
      this.props.onChangeRowsPerPage(newLimit);
    }
  };

  getSelectedUser(row) {
    const { type } = this.props;
    if (type === 'enroll') return row.user || {};
    if (type === 'site_visit') return row.bidder_info || {};

    return {};
  }

  getColumns() {
    const {
      type,
      pendingTab,
      selectedIds = [],
      onCheckboxChange,
      onCheckAll,
      payments = [],
      currentPage = 1,
      limit = 15,
    } = this.props;

    const checkboxColumn = {
      name: (
        <input
          type="checkbox"
          checked={
            payments.length > 0 &&
            selectedIds.length > 0 &&
            selectedIds.length === payments.filter((r) => !r.refund_not_eligible).length
          }
          onChange={() => onCheckAll(payments)}
          aria-label="Select all eligible rows"
        />
      ),
      cell: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.id)}
          onChange={() => onCheckboxChange(row)}
          aria-label={`Select row ${row.id}`}
        />
      ),
      width: '50px',
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    };

    const baseColumns = [
      {
        name: '#',
        width: '70px',
        sortable: false,
        cell: (_, index) => (currentPage - 1) * limit + (index + 1),
      },
      {
        name: 'ID',
        selector: (row) => row.id,
        sortable: true,
        width: '80px',
      },
      {
        name: i18n.t('payment'),
        width: '110px',
        cell: (row) => {
          if (type === 'enroll') return types[row.pay_type] || '-';
          if (type === 'site_visit') return types[row.payment_type] || '-';
          return '-';
        },
        sortable: true,
      },
      {
        name: i18n.t('User ID'),
        width: '160px',
        sortable: true,
        cell: (row) => {
          const selected_user = this.getSelectedUser(row);
          return (
            <div className="table-row-l2">
              <span>{selected_user.id || '-'}</span>

              <Tooltip
                icon={Info}
                tooltipText={i18n.t('user_info')}
                onClick={() => this.props.showUserInfo(selected_user)}
                size={20}
              />
            </div>
          );
        },
      },
      {
        name: i18n.t('name'),
        sortable: true,
        width: '160px',
        cell: (row) => (
          <div
            style={{
              fontSize: '13px',
            }}
          >
            {type === 'enroll' ? row.user?.name || '-' : row.bidder_info?.name || '-'}
          </div>
        ),
      },
      {
        name: i18n.t('group'),
        cell: (row) => {
          if (type === 'enroll') return row.group?.group_name || '-';
          if (type === 'site_visit') return row.group_info?.group_name || '-';
          return '-';
        },
        sortable: true,
        width: '130px',
      },
      {
        name: i18n.t('mzad_id'),
        sortable: true,
        width: '100px',
        cell: (row) => row.auction_id,
      },
      {
        name: i18n.t('auction'),
        sortable: true,
        width: '160px',
        cell: (row) => (
          <div
            style={{
              fontSize: '13px',
            }}
            title={row.auction?.title}
          >
            {row.auction?.title || '-'}
          </div>
        ),
      },
      {
        name: i18n.t('price'),
        selector: (row) => row.refund_amount ?? '-',
        sortable: true,
      },
      {
        name: i18n.t('hold_balance'),
        selector: (row) => row.user.hold_amount ?? '-',
        sortable: true,
      },
      {
        name: i18n.t('payment_date'),
        sortable: true,
        width: '160px',
        center: true,
        cell: (row) => (
          <div
            style={{
              fontSize: '13px',
              color: row.created_at ? '#374151' : '#9ca3af',
            }}
            title={row.created_at}
          >
            {formatDate(row.created_at)}
          </div>
        ),
      },
      {
        name: i18n.t('refund'),
        selector: (row) => row.rank || 0,
        sortable: true,
        width: '110px',
        center: true,
        cell: (row) => {
          const isNotEligible = row.refund_not_eligible;

          return (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '0.3rem',
                fontWeight: 500,
                fontSize: '12px',
                backgroundColor: isNotEligible ? '#ffe4e6' : '#dcfce7',
                color: isNotEligible ? '#b91c1c' : '#166534',
              }}
            >
              {isNotEligible ? 'Not-Eligible' : 'Eligible'}
            </span>
          );
        },
      },
      {
        name: i18n.t('rank'),
        width: '110px',
        center: true,
        cell: (row) => (row.rank ? row.rank : 'No Bid Placed'),
        sortable: true,
      },
      {
        name: i18n.t('group_enroll'),
        width: '110px',
        center: true,
        cell: (row) =>
          row.is_grouped_enroll ? (
            <Check size={16} className="icon-true" />
          ) : (
            <X size={16} className="icon-false" />
          ),
        sortable: true,
      },
      {
        name: i18n.t('status'),
        sortable: true,
        width: '200px',
        center: true,
        cell: (row) => <StatusBadge status={row.refund_status} />,
      },
    ];

    if (pendingTab) {
      return [checkboxColumn, ...baseColumns];
    }
    return baseColumns;
  }
  render() {
    const { payments = [], currentPage = 1, limit = 15, total = 0 } = this.props;

    return (
      <>
        <DataTable
          key={`${currentPage}-${limit}`}
          title=""
          columns={this.getColumns()}
          data={payments}
          customStyles={tableStyles}
          highlightOnHover
          pagination
          paginationServer
          paginationTotalRows={total}
          paginationPerPage={limit}
          paginationDefaultPage={currentPage}
          paginationRowsPerPageOptions={[15, 25, 50, 100]}
          paginationComponentOptions={{
            rowsPerPageText: i18n.t('rows_per_page'),
            rangeSeparatorText: i18n.t('out'),
          }}
          onChangeRowsPerPage={this.handleRowsPerPageChange}
          onChangePage={this.handlePageChange}
        />
      </>
    );
  }
}
